import type { GetPageTargetsRoute } from "@/app/api/pages/targets/route";
import {
  MAX_PAGE_TARGETS_PER_REQUEST,
  type PageTarget,
} from "@/components/analysis/agentic/pages/pageTarget";
import { postJson, RouteError } from "@/utils/routeUtils";
import { queryOptions } from "@tanstack/react-query";
import { create, windowedFiniteBatchScheduler } from "@yornaath/batshit";

type PageTargetResolution = GetPageTargetsRoute["output"]["results"][number];

const targetKey = (target: PageTarget) => JSON.stringify(target);

// Coalesces the links rendered in one frame, e.g. a chat message with several pages, into one POST.
const pageTargetBatcher = create<
  { key: string; result: PageTargetResolution }[],
  PageTarget,
  PageTargetResolution
>({
  fetcher: async (targets) => {
    const result = await postJson("/api/pages/targets", { targets });
    if (!result.success) throw new RouteError(result.error, result.statusCode);
    return result.data.results.map((resolution, index) => ({
      key: targetKey(targets[index]),
      result: resolution,
    }));
  },
  resolver: (items, target) => {
    const item = items.find(({ key }) => key === targetKey(target));
    if (!item) throw new Error("Page target missing from the response");
    return item.result;
  },
  scheduler: windowedFiniteBatchScheduler({
    windowMs: 0,
    maxBatchSize: MAX_PAGE_TARGETS_PER_REQUEST,
  }),
});

export const pageTargetOptions = (target: PageTarget) =>
  queryOptions({
    queryKey: ["page-target", target] as const,
    queryFn: () => pageTargetBatcher.fetch(target),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      const isClientError =
        error instanceof RouteError && error.statusCode < 500;
      return !isClientError && failureCount < 3;
    },
  });
